import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const PostLaminectomySyndromeCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Post-Laminectomy Syndrome in New York City",
    metaDescription: "Comprehensive care for post-laminectomy syndrome in New York. Discover treatments to alleviate pain and improve mobility after spinal surgery at NY Center for Spinal Disorders.",
    metaKeywords: "post-laminectomy syndrome, failed back surgery treatment NYC, spinal pain relief, Dr. Shah, Dr. Tindel, pain management",
    heroDescription: "Expert treatment for post-laminectomy syndrome with personalized care to restore mobility and reduce discomfort.",
    forWho: [
      "A laminectomy is a surgical procedure that removes part of the vertebra called the lamina to relieve pressure on the spinal cord or nerves. Post-Laminectomy Syndrome refers to the persistence or recurrence of pain after this type of surgery. Sometimes, scar tissue develops around nerve roots, or the original source of pain wasn’t completely resolved. In other instances, the spine may become unstable, leading to new discomfort.",
      "Though it can be discouraging, having ongoing pain after laminectomy doesn’t necessarily mean no solutions are left. Each case is unique, and identifying the exact cause is key to improving outcomes."
    ],
    conditionInfo: [
      "If you’re experiencing persistent or worsening back or leg pain after a laminectomy, reach out to the NY Center for Spinal Disorders. Our skilled spine surgeons can perform advanced imaging, assess your surgical history, and explore whether additional interventions or revision surgery might help.",
    ],
    treatment: [
      "Options include physical therapy, pain management injections, or minimally invasive revision surgery if needed.",
      "A structured rehabilitation plan promotes gradual improvements.",
      "Many regain mobility and reduce reliance on pain medications."
    ],
    postCare: [
      "We provide continuous monitoring to ensure progress and tackle concerns early.",
      "Supportive care includes lifestyle guidance, ergonomic advice, and emotional support."
    ],
    nonSurgicalOptions: [
      "Physical therapy",
      "Nerve blocks or epidural injections",
      "Pain management programs"
    ],
    faqs: [
      {
        id: 1,
        question: "Is revision surgery guaranteed to help?",
        answer: "Success depends on underlying causes, but many benefit when issues are accurately identified."
      },
      {
        id: 2,
        question: "Can therapy alone fix post-laminectomy pain?",
        answer: "In mild cases, comprehensive non-surgical approaches can yield substantial improvement."
      }
    ]
  };  

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
      <title>{pageData.title} - NYC for Spinal Disorders</title>
      <meta name="description" content={pageData.metaDescription} />
      <meta name="keywords" content={pageData.metaKeywords} />
      <meta name="author" content="New York Center for Spinal Disorders" />
      <meta property="og:title" content={pageData.title} />
      <meta property="og:description" content={pageData.metaDescription} />
      <meta property="og:image" content="/images/logo-header.png" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PostLaminectomySyndromeCondition;